// Runtime tenant theming: generate the primary scale + brand-hued neutral scale from one base hex
// and write them to :root, where every Tailwind utility and PrimeVue token reads them via var().
//
// - Primary scale: PrimeVue's own `palette` generator (the same one its preset uses).
// - Neutrals + contrast: tinycolor2, because brand-hued neutrals need HSL — saturation and lightness
//   controlled independently, which sRGB mixing can't do (mixing a dark seed into light gray darkens
//   it instead of just tinting it).
import { palette } from '@primevue/themes';
import tinycolor from 'tinycolor2';

// Neutral: a fixed gray lightness ramp (Tailwind's neutral) re-tinted to the brand hue at a low,
// constant saturation — so surfaces carry a hint of brand color while keeping their lightness.
const NEUTRAL_GRAY = {
  50: '#fafafa',
  100: '#f5f5f5',
  200: '#e5e5e5',
  300: '#d4d4d4',
  400: '#a3a3a3',
  500: '#737373',
  600: '#525252',
  700: '#404040',
  800: '#262626',
  900: '#171717',
  950: '#0a0a0a',
};
const NEUTRAL_SAT = 0.1; // brand-hue saturation of the neutrals (0 = pure gray)
const neutral = (step, hue) =>
  tinycolor({ h: hue, s: NEUTRAL_SAT, l: tinycolor(NEUTRAL_GRAY[step]).toHsl().l }).toHexString();

// Text that sits ON the primary (button labels, primary tags): white fails on a light brand
// (e.g. yellow), so pick white or a dark brand ink — whichever is more readable on primary-500.
function onPrimary(seed) {
  const ink = tinycolor(seed).darken(35).toHexString();
  return tinycolor.readability(seed, '#ffffff') >= tinycolor.readability(seed, ink) ? '#ffffff' : ink;
}

// Links / text-buttons sit on the card. A brand color often can't be read there as-is, so push it
// away from the surface — darker on a light card, lighter on a dark one — until it clears AA
// (4.5:1). A no-op for a brand that already passes (e.g. green on white).
function accessibleOn(seed, bg) {
  const darken = tinycolor(bg).getLuminance() > 0.5;
  const c = tinycolor(seed);
  for (let i = 0; i < 15 && tinycolor.readability(c, bg) < 4.5; i += 1) {
    if (darken) c.darken(4);
    else c.lighten(4);
  }
  return c.toHexString();
}

function isValidHex(hex) {
  return /^#[0-9a-fA-F]{6}$/.test(hex);
}

// Bulma derives its primary from separate hue/saturation/lightness custom properties, so Buefy
// components need the brand color in that shape on top of the design-system tokens.
function applyBulmaPrimary(color) {
  const { h, s, l } = tinycolor(color).toHsl();
  const root = document.documentElement.style;
  root.setProperty('--bulma-primary-h', h);
  root.setProperty('--bulma-primary-s', `${s * 100}%`);
  root.setProperty('--bulma-primary-l', `${l * 100}%`);
}

// The design-system half: everything the tokens in assets/css/tokens.css expect. The styleguide
// showcase calls this directly (design_system/App.vue) to preview a brand.
export function applyPrimary(seed, dark = false) {
  if (!isValidHex(seed)) return;
  const root = document.documentElement.style;
  const pal = palette(seed);
  for (const [step, hex] of Object.entries(pal)) {
    root.setProperty(`--color-primary-${step}`, hex);
  }
  // Tint the elevation shadow toward the brand's darkest shade (near-black with the brand hue).
  const { r, g, b } = tinycolor(pal[950]).toRgb();
  root.setProperty('--shadow-color', `${r} ${g} ${b}`);
  const hue = tinycolor(seed).toHsl().h;
  for (const step of Object.keys(NEUTRAL_GRAY)) {
    root.setProperty(`--color-neutral-${step}`, neutral(step, hue));
  }
  root.setProperty('--color-primary-contrast', onPrimary(seed));
  // Links sit on the card: white in light mode, neutral-900 in dark — clamp against whichever.
  const bg_card = dark ? neutral(900, hue) : '#ffffff';
  root.setProperty('--color-link', accessibleOn(seed, bg_card));
}

// What the app calls once the tenant is known.
export default function applyTheme(theme_primary_color) {
  const color = tinycolor(theme_primary_color);
  if (!color.isValid()) return;
  applyPrimary(color.toHexString());
  applyBulmaPrimary(color);
}
